import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Shield, Link as LinkIcon, User, Briefcase, Building, Mail, Phone, Camera, Award, Trophy, FileText, MapPin, DollarSign, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import {
  getFieldGovernance,
  bulkUpdateFieldGovernance,
  type ProfileFieldGovernanceView,
  type ProfileFieldGovernanceInput,
  FieldGovernanceStatus,
} from '../../../api/PublicProfile/publicProfileApi';

interface FieldGovernanceTabProps {
  selectedPersonaId: string;
  onSettingsChange?: () => void;
}

export interface FieldGovernanceTabRef {
  save: () => Promise<boolean>;
  hasChanges: () => boolean;
}

// Standard field keys that the system supports
const STANDARD_FIELDS = [
  { key: 'name', label: 'Name', icon: 'User' },
  { key: 'jobTitle', label: 'Job Title', icon: 'Briefcase' },
  { key: 'department', label: 'Department', icon: 'Building' },
  { key: 'email', label: 'Email', icon: 'Mail' },
  { key: 'phone', label: 'Phone', icon: 'Phone' },
  { key: 'photo', label: 'Photo', icon: 'Camera' },
  { key: 'skills', label: 'Skills', icon: 'Award' },
  { key: 'achievements', label: 'Achievements', icon: 'Trophy' },
  { key: 'socialLinks', label: 'Social Links', icon: 'Link' },
  { key: 'bio', label: 'Bio', icon: 'FileText' },
  { key: 'location', label: 'Location', icon: 'MapPin' },
  { key: 'salary', label: 'Salary Information', icon: 'DollarSign' },
];

const FieldGovernanceTab = forwardRef<FieldGovernanceTabRef, FieldGovernanceTabProps>(({ selectedPersonaId, onSettingsChange }, ref) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState<ProfileFieldGovernanceView[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const loadData = useCallback(async () => {
    if (!selectedPersonaId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const personaId = parseInt(selectedPersonaId, 10);
      const result = await getFieldGovernance(personaId);

      if (result.status === 200 && result.payload) {
        // If no governance rules exist yet, create default ones for all standard fields
        if (result.payload.length === 0) {
          const defaults = STANDARD_FIELDS.map((field, index) => ({
            id: -(index + 1), // Negative IDs for unsaved fields
            personaId: personaId,
            fieldKey: field.key,
            status: FieldGovernanceStatus.Allowed,
            isMandatory: field.key === 'name' || field.key === 'jobTitle',
            requiresHRApproval: false,
            requiresManagerApproval: false,
          }));
          setFields(defaults);
        } else {
          // Merge with standard fields (ensure all standard fields are represented)
          const existingKeys = new Set(result.payload.map(f => f.fieldKey));
          const merged = [...result.payload];

          STANDARD_FIELDS.forEach((field, index) => {
            if (!existingKeys.has(field.key)) {
              merged.push({
                id: -(index + 1),
                personaId: personaId,
                fieldKey: field.key,
                status: FieldGovernanceStatus.Allowed,
                isMandatory: false,
                requiresHRApproval: false,
                requiresManagerApproval: false,
              });
            }
          });

          setFields(merged);
        }
      } else {
        showToast('error', t('Failed to load field governance settings'));
      }
    } catch (error) {
      console.error('Error loading field governance:', error);
      showToast('error', t('Failed to load field governance settings'));
    } finally {
      setLoading(false);
    }
  }, [selectedPersonaId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!hasChanges) return true;
      return await handleSaveChanges();
    },
    hasChanges: () => hasChanges,
  }));

  // Helper to mark changes and notify parent
  const markAsChanged = () => {
    setHasChanges(true);
    onSettingsChange?.();
  };

  const handleStatusChange = (fieldKey: string, newStatus: FieldGovernanceStatus) => {
    setFields(prev => prev.map(field =>
      field.fieldKey === fieldKey ? { ...field, status: newStatus } : field
    ));
    markAsChanged();
  };

  const handleMandatoryToggle = (fieldKey: string) => {
    setFields(prev => prev.map(field =>
      field.fieldKey === fieldKey ? { ...field, isMandatory: !field.isMandatory } : field
    ));
    markAsChanged();
  };

  const handleHRApprovalToggle = (fieldKey: string) => {
    setFields(prev => prev.map(field =>
      field.fieldKey === fieldKey ? { ...field, requiresHRApproval: !field.requiresHRApproval } : field
    ));
    markAsChanged();
  };

  const handleManagerApprovalToggle = (fieldKey: string) => {
    setFields(prev => prev.map(field =>
      field.fieldKey === fieldKey ? { ...field, requiresManagerApproval: !field.requiresManagerApproval } : field
    ));
    markAsChanged();
  };

  const handleSaveChanges = async (): Promise<boolean> => {
    if (!selectedPersonaId) return false;

    setSaving(true);
    try {
      const personaId = parseInt(selectedPersonaId, 10);
      const inputs: ProfileFieldGovernanceInput[] = fields.map(f => ({
        fieldKey: f.fieldKey,
        status: f.status,
        isMandatory: f.isMandatory,
        requiresHRApproval: f.requiresHRApproval,
        requiresManagerApproval: f.requiresManagerApproval,
      }));

      const result = await bulkUpdateFieldGovernance(personaId, inputs);
      if (result.status === 200 && result.payload) {
        setFields(result.payload);
        setHasChanges(false);
        showToast('success', t('Field governance settings saved successfully'));
        onSettingsChange?.();
        return true;
      } else {
        showToast('error', t('Failed to save field governance settings'));
        return false;
      }
    } catch (error) {
      console.error('Error saving field governance:', error);
      showToast('error', t('Failed to save field governance settings'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: FieldGovernanceStatus) => {
    switch (status) {
      case FieldGovernanceStatus.Allowed:
        return 'bg-success/10 text-success';
      case FieldGovernanceStatus.Hidden:
        return 'bg-destructive/10 text-destructive';
      case FieldGovernanceStatus.Forbidden:
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getIconComponent = (iconName: string) => {
    const icons: Record<string, React.ComponentType<{ className?: string }>> = {
      User, Briefcase, Building, Mail, Phone, Camera, Award, Trophy, Link: LinkIcon, FileText, MapPin, DollarSign
    };
    return icons[iconName] || LinkIcon;
  };

  const getFieldInfo = (fieldKey: string) => {
    return STANDARD_FIELDS.find(f => f.key === fieldKey) || { key: fieldKey, label: fieldKey, icon: 'FileText' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ms-3 text-muted-foreground">{t('Loading field governance settings...')}</span>
      </div>
    );
  }

  if (!selectedPersonaId) {
    return (
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-warning" />
          <p className="text-sm text-warning">
            {t('Please select a persona to configure field governance settings.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2
                        ${toast.type === 'success'
                          ? 'bg-success/10 text-success'
                          : 'bg-destructive/10 text-destructive'}`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Persona Info Banner */}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-primary" />
          <p className="text-sm text-primary">
            {t('You are configuring settings for the selected persona. Changes will only apply to this persona.')}
          </p>
        </div>
      </div>

      {/* Field-Level Governance */}
      <Card variant="elevated">
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center">
              <Shield className="w-5 h-5 me-2" />
              {t('Field-Level Governance')}
            </h2>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-success rounded-full"></span>
                <span className="text-muted-foreground">{t('Allowed')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-destructive rounded-full"></span>
                <span className="text-muted-foreground">{t('Hidden')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-muted-foreground rounded-full"></span>
                <span className="text-muted-foreground">{t('Forbidden')}</span>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Field')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Status')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Mandatory if Public')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Update Requires HR Approval')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t("Update Requires Manager's Approval")}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {t('Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {fields.map((field) => {
                  const fieldInfo = getFieldInfo(field.fieldKey);
                  const IconComponent = getIconComponent(fieldInfo.icon);
                  const isHidden = field.status === FieldGovernanceStatus.Hidden || field.status === FieldGovernanceStatus.Forbidden;

                  return (
                    <tr key={field.fieldKey} className="hover:bg-muted/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <IconComponent className="w-4 h-4 text-muted-foreground me-3" />
                          <span className="text-sm font-medium text-foreground">{t(fieldInfo.label)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(field.status)}`}>
                          {t(field.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          checked={field.isMandatory}
                          onChange={() => handleMandatoryToggle(field.fieldKey)}
                          disabled={isHidden}
                          className="w-4 h-4 text-primary rounded focus-visible:ring-ring disabled:opacity-30"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          checked={field.requiresHRApproval}
                          onChange={() => handleHRApprovalToggle(field.fieldKey)}
                          disabled={isHidden}
                          className="w-4 h-4 text-primary rounded focus-visible:ring-ring disabled:opacity-30"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          checked={field.requiresManagerApproval}
                          onChange={() => handleManagerApprovalToggle(field.fieldKey)}
                          disabled={isHidden}
                          className="w-4 h-4 text-primary rounded focus-visible:ring-ring disabled:opacity-30"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleStatusChange(field.fieldKey, FieldGovernanceStatus.Allowed)}
                            className={`px-3 py-1 text-xs rounded-lg transition-colors min-h-[44px] ${
                              field.status === FieldGovernanceStatus.Allowed ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground hover:bg-success/5'
                            }`}
                          >
                            {t('Allow')}
                          </button>
                          <button
                            onClick={() => handleStatusChange(field.fieldKey, FieldGovernanceStatus.Hidden)}
                            className={`px-3 py-1 text-xs rounded-lg transition-colors min-h-[44px] ${
                              field.status === FieldGovernanceStatus.Hidden ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground hover:bg-destructive/5'
                            }`}
                          >
                            {t('Hide')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Content Validation Rules */}
      <Card variant="elevated">
        <CardContent>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 me-2" />
            {t('Content Validation Rules')}
          </h2>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-foreground">{t('Profanity Filter')}</p>
                <input type="checkbox" defaultChecked className="w-4 h-4 text-primary rounded focus-visible:ring-ring" />
              </div>
              <p className="text-sm text-muted-foreground">{t('Automatically flag profiles with inappropriate language')}</p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-foreground">{t('Email Validation')}</p>
                <input type="checkbox" defaultChecked className="w-4 h-4 text-primary rounded focus-visible:ring-ring" />
              </div>
              <p className="text-sm text-muted-foreground">{t('Ensure email addresses are valid and from company domain')}</p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-foreground">{t('URL Validation')}</p>
                <input type="checkbox" defaultChecked className="w-4 h-4 text-primary rounded focus-visible:ring-ring" />
              </div>
              <p className="text-sm text-muted-foreground">{t('Validate social media and website URLs')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

FieldGovernanceTab.displayName = 'FieldGovernanceTab';

export default FieldGovernanceTab;
